import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import WarningIcon from '@mui/icons-material/Warning'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Collapse from '@mui/material/Collapse'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import KNCaption from 'components/KNCaption/KNCaption'
import KNForm from 'components/KNForm/KNForm'
import { Role } from 'context/authentication/Role.types'
import { getRouteName } from 'global/helpers/activeRoute'
import { analyticsEvent } from 'global/helpers/analytics'
import { hasAccess } from 'global/helpers/authorization'
import { getGlobalErrorMessages, processServerErrorMessages } from 'global/helpers/form'
import useDialog from 'global/hooks/useDialog'
import { type ReactElement, useEffect, useState } from 'react'
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import LocationLink from 'screens/TripDashboard/LocationLink'
import { TRIPS_QUERY_KEY } from 'screens/TripDashboard/TripDashboard.service'
import type { TripData } from 'screens/TripDashboard/TripDashboard.types'
import StopHeader from 'screens/TripDetails/StopHeader'
import { TRIP_DETAILS_QUERY_KEY, tripLegsQueryOptions, tripLogsQueryOptions } from 'screens/TripDetails/TripDetails.service'
import { type AvailableStopStatus, type LegData, type StopData, WorkflowType } from 'screens/TripDetails/TripDetails.types'
import DocumentLink from './DocumentLink'
import DocumentsUploadDialog from './DocumentsUploadDialog'
import DocumentsUploadFields from './DocumentsUploadFields'
import {
  getDefaultStatus,
  getDefaultStatusDate,
  getDefaultStatusTime,
  getTimezoneReference,
  getTruckZoneMessage,
  getUpdatedStopForUpdateStopStatusWithAttachments,
  updateTripLegsCacheWithUpdatedStops,
} from './StatusManager.helpers'
import { updateStopStatusWithAttachments } from './StatusManager.service'
import type { StatusUpdateWithAttachmentsFormValues } from './StatusManager.types'
import StatusUpdateFields from './StatusUpdateFields'

interface QuickStatusUpdateStopProps {
  trip: TripData
  leg: LegData
  stop: StopData
  expanded: boolean
  onChange: (panel: string) => void
}

const QuickStatusUpdateStop = ({
  trip,
  leg,
  stop,
  expanded,
  onChange,
}: QuickStatusUpdateStopProps): ReactElement => {
  const theme = useTheme()
  const { t } = useTranslation()
  const location = useLocation()
  const [pendingFiles, setPendingFiles] = useState(false)
  const form = useForm<StatusUpdateWithAttachmentsFormValues>({
    defaultValues: {
      wayPointType: stop.type,
      statusId: getDefaultStatus(stop.availableStatuses),
      createdAtDate: getDefaultStatusDate(stop, leg),
      createdAtTime: getDefaultStatusTime(stop, leg),
      shipmentNumber: leg.shipmentNumber,
      paperless: false,
    },
    criteriaMode: 'all',
  })
  const { handleSubmit, watch, formState, setError } = form
  const watchedStatusId = watch('statusId')
  const [selectedStatus, setSelectedStatus] = useState<AvailableStopStatus>()
  const documentsUploadDialog = useDialog<typeof DocumentsUploadDialog>()
  const tripLogsQuery = useQuery({
    ...tripLogsQueryOptions(trip.entityId),
    enabled: expanded,
  })
  const truckZoneMessage = getTruckZoneMessage(
    tripLogsQuery.data ?? [],
    stop.type,
    stop.wayPointCid,
  ) ?? undefined
  const updateStatusMutation = useMutation({
    mutationFn: (data: StatusUpdateWithAttachmentsFormValues) =>
      updateStopStatusWithAttachments(trip.entityId, stop.wayPointCid, data),
    onSuccess: async (_response, data, _result, context) => {
      const updatedStop = getUpdatedStopForUpdateStopStatusWithAttachments(stop, data)
      const queryKey = tripLegsQueryOptions(trip.entityId).queryKey
      await updateTripLegsCacheWithUpdatedStops([updatedStop], queryKey, context.client)
      setTimeout(() => {
        void Promise.all([
          context.client.invalidateQueries({ queryKey: [TRIP_DETAILS_QUERY_KEY, trip.entityId] }),
          context.client.invalidateQueries({ queryKey: [TRIPS_QUERY_KEY] }),
        ])
      }, 2000)
      analyticsEvent('polestar_cs_status_with_attachments_updated', [
        getRouteName(location.pathname),
        data.paperless ? 'paperless' : 'regular',
      ])
    },
    onError: (error) => setError('root', processServerErrorMessages(error)),
  })

  useEffect(() => {
    setSelectedStatus(stop.availableStatuses.find((status) => status.id === watchedStatusId))
  }, [watchedStatusId, stop.availableStatuses])

  const onSubmit: SubmitHandler<StatusUpdateWithAttachmentsFormValues> = (
    data: StatusUpdateWithAttachmentsFormValues,
  ) => {
    updateStatusMutation.mutate(data)
  }

  return (
    <>
      <FormProvider {...form}>
        <KNForm onSubmit={onSubmit}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            onClick={(): void => {
              onChange(stop.wayPointCid)
            }}
            px={2}
            py={1.5}
            sx={{
              cursor: 'pointer',
            }}
          >
            <StopHeader leg={leg} stop={stop} />
            <ExpandMoreIcon
              sx={{
                color: 'primary.light',
                transform: expanded ? 'rotate(-180deg)' : 'rotate(0deg)',
                transition: theme.transitions.create('transform', {
                  duration: theme.transitions.duration.shortest,
                }),
              }}
            />
          </Stack>
          {formState.errors?.root && (
            <Alert severity="error">{getGlobalErrorMessages(formState.errors.root)}</Alert>
          )}
          <Collapse in={expanded}>
            <Stack spacing={2} pl={6.5} pr={2} pb={2}>
              <Box>
                <Typography variant="p4" color="primary.main">{stop.address.name.join(' ')}</Typography>
                <LocationLink
                  countryCode={stop.address.countryCode}
                  city={stop.address.city.join(', ')}
                  zipCode={stop.address.zipCode}
                  street={stop.address.street.join(', ')}
                  marker={stop.geoPoint ? {
                    id: stop.wayPointCid,
                    latitude: stop.geoPoint.latitude,
                    longitude: stop.geoPoint.longitude,
                    type: stop.type,
                  } : undefined}
                  lineBreaks="street"
                />
              </Box>
              {stop.documents && (
                <Box>
                  <Typography variant="p6" component="p" color="primary.main">
                    {t('screens.cs.trip_details.card.attachments')}
                  </Typography>
                  <Typography variant="p6" color="text.main">
                    {stop.documents.map((file) => (
                      <DocumentLink key={file.id} file={file} />
                    ))}
                  </Typography>
                </Box>
              )}
              {hasAccess(Role.Editor) && (
                <>
                  {stop.availableStatuses.length > 0 && (
                    <>
                      <StatusUpdateFields
                        availableStatuses={stop.availableStatuses}
                        hasProof={stop.isProofAttached}
                        showPaperless={leg.paperlessAvailable}
                        truckZoneMessage={truckZoneMessage}
                        timezoneReference={getTimezoneReference(stop, leg)}
                        usePreciseTime={leg.workflowType === WorkflowType.SECURE_CLOUD_DEPLOYMENT}
                      />
                      <DocumentsUploadFields
                        stopType={stop.type}
                        showProof={!!selectedStatus && selectedStatus.proofs !== 'DISABLED'}
                        hasProof={stop.isProofAttached}
                        showPhotos={!!selectedStatus && selectedStatus.attachments !== 'DISABLED'}
                        showPalletsDocument={stop.palletsDocumentRequired}
                        hasPalletsDocument={stop.palletsDocumentAttached}
                        showTro={leg.troAvailable}
                        hasTro={stop.documents?.find((document) => document.fileType === 'TRO') !== undefined}
                        onPending={setPendingFiles}
                      />
                    </>
                  )}
                  <Stack
                    spacing={1}
                    direction="column"
                    alignItems={{ xs: 'stretch', sm: 'start' }}
                  >
                    {stop.availableStatuses.length > 0 ? (
                      <>
                        {stop.type === 'CUS' && (
                          <KNCaption iconColor="warning" icon={<WarningIcon />}>
                            {t('screens.cs.trip_details.update_status.customs_local_time')}
                          </KNCaption>
                        )}
                        <KNCaption iconColor="warning" icon={<WarningIcon />}>
                          {t('screens.cs.trip_details.update_status.warning')}
                        </KNCaption>
                        <Button
                          type="submit"
                          color="primary"
                          size="small"
                          variant="contained"
                          loading={formState.isSubmitting || updateStatusMutation.isPending || pendingFiles}
                          onClick={handleSubmit(onSubmit)}
                        >
                          {t('screens.cs.trip_details.card.actions.update_status_and_documents')}
                        </Button>
                      </>
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        onClick={documentsUploadDialog.handleOpen}
                      >
                        {t('screens.cs.trip_details.card.actions.upload_documents')}
                      </Button>
                    )}
                  </Stack>
                </>
              )}
            </Stack>
          </Collapse>
        </KNForm>
      </FormProvider>

      <DocumentsUploadDialog
        payload={{
          trip,
          leg,
          stop,
        }}
        open={documentsUploadDialog.isOpen}
        onAction={documentsUploadDialog.handleAction}
        onClose={documentsUploadDialog.handleClose}
      />
    </>
  )
}

export default QuickStatusUpdateStop
